import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { Linkedin, Facebook, Twitter, Mail } from 'lucide-react';
import { onContactLinkClick } from '@/lib/contactLinks';
import './StaggeredMenu.css';

/**
 * StaggeredMenu — GSAP-driven side panel.
 *
 * Design goals:
 *   - Toggle at top-left ("Menu" ↔ "Close") with a +/× icon
 *   - Two coloured "prelayers" (deep navy, deep red) slide in before the panel
 *   - Panel items reveal on a stagger with the numbering fading in first
 *   - Panel closes on outside click when closeOnClickAway is set
 *
 * Props:
 *   items         [{ id, label }] — item objects, order preserved
 *   accent        css colour for numbering + accents (default red)
 *   closeOnClickAway
 *   onItemClick(id)
 */
export const StaggeredMenu = ({
  items = [],
  accent = '#EF4444',
  closeOnClickAway = true,
  onItemClick,
}) => {
  const rootRef = useRef(null);
  const p1Ref = useRef(null);
  const p2Ref = useRef(null);
  const panelRef = useRef(null);
  const itemsRef = useRef([]);
  const numRefs = useRef([]);
  const connectTitleRef = useRef(null);
  const connectRowRef = useRef(null);
  const tlRef = useRef(null);
  const [open, setOpen] = useState(false);

  // Assign refs into arrays via callbacks below.
  itemsRef.current = [];
  numRefs.current = [];

  useEffect(() => {
    if (!closeOnClickAway) return undefined;
    const onDoc = (e) => {
      if (!open) return;
      if (rootRef.current && !panelRef.current.contains(e.target) && e.target.classList?.contains('sm-scrim')) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open, closeOnClickAway]);

  useEffect(() => {
    if (!panelRef.current) return;
    if (tlRef.current) tlRef.current.kill();
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
    if (open) {
      tl.to(p1Ref.current, { xPercent: 100, duration: 0.55 }, 0);
      tl.to(p2Ref.current, { xPercent: 100, duration: 0.55 }, 0.1);
      tl.to(panelRef.current, { xPercent: 100, duration: 0.5 }, 0.2);
      tl.to(
        itemsRef.current,
        { y: 0, opacity: 1, duration: 0.5, stagger: 0.06 },
        0.35
      );
      tl.to(
        numRefs.current,
        { y: 0, opacity: 1, duration: 0.4, stagger: 0.06 },
        0.4
      );
      tl.to(connectTitleRef.current, { opacity: 1, duration: 0.3 }, 0.7);
      tl.to(connectRowRef.current, { opacity: 1, y: 0, duration: 0.35 }, 0.75);
    } else {
      tl.to(connectRowRef.current, { opacity: 0, duration: 0.2 }, 0);
      tl.to(connectTitleRef.current, { opacity: 0, duration: 0.2 }, 0);
      tl.to(itemsRef.current, { y: 24, opacity: 0, duration: 0.3, stagger: 0.02 }, 0);
      tl.to(numRefs.current, { y: -8, opacity: 0, duration: 0.25 }, 0);
      tl.to(panelRef.current, { xPercent: -105, duration: 0.45 }, 0.1);
      tl.to(p2Ref.current, { xPercent: -105, duration: 0.45 }, 0.15);
      tl.to(p1Ref.current, { xPercent: -105, duration: 0.45 }, 0.2);
    }
    tlRef.current = tl;
  }, [open]);

  const handleItemClick = (id) => {
    setOpen(false);
    if (onItemClick) onItemClick(id);
  };

  return (
    <div
      ref={rootRef}
      className={`sm-root ${open ? 'is-open' : ''}`}
      style={{ '--sm-accent': accent }}
      data-testid="staggered-menu-root"
    >
      <button
        type="button"
        className="sm-toggle"
        onClick={() => setOpen((v) => !v)}
        data-testid="menu-toggle"
        aria-expanded={open}
      >
        <span className={`sm-icon ${open ? 'is-open' : ''}`} aria-hidden />
        <span className={`sm-label-track ${open ? 'is-open' : ''}`}>
          <span>Menu</span>
          <span>Close</span>
        </span>
      </button>

      <div className="sm-scrim" onClick={() => setOpen(false)} />

      <div ref={p1Ref} className="sm-prelayer p1" aria-hidden />
      <div ref={p2Ref} className="sm-prelayer p2" aria-hidden />

      <nav ref={panelRef} className="sm-panel" aria-label="Primary">
        {items.map((it, i) => (
          <div
            key={it.id}
            ref={(el) => { if (el) itemsRef.current[i] = el; }}
            className="sm-item-row"
            onClick={() => handleItemClick(it.id)}
            data-testid={`menu-item-${it.id}`}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') handleItemClick(it.id); }}
          >
            <span
              className="sm-num"
              ref={(el) => { if (el) numRefs.current[i] = el; }}
            >
              {String(i + 1).padStart(2, '0')}
            </span>
            <span>{it.label}</span>
          </div>
        ))}

        <div ref={connectTitleRef} className="sm-connect-title">Connect</div>
        <div ref={connectRowRef} className="sm-connect-row">
          <a href="https://www.linkedin.com/" target="_blank" rel="noreferrer" aria-label="LinkedIn"><Linkedin className="w-4 h-4" /></a>
          <a href="https://www.facebook.com/" target="_blank" rel="noreferrer" aria-label="Facebook"><Facebook className="w-4 h-4" /></a>
          <a href="https://twitter.com/" target="_blank" rel="noreferrer" aria-label="Twitter"><Twitter className="w-4 h-4" /></a>
          <a href="mailto:mishaan@radiusconsulting.co.za" onClick={onContactLinkClick} aria-label="Email"><Mail className="w-4 h-4" /></a>
        </div>
      </nav>
    </div>
  );
};

export default StaggeredMenu;
